import type { CampaignRecipientInput } from '../types'

const EMAIL_RE = /^[^\s<>,]+@[^\s<>,]+\.[^\s<>,]+$/

// Minimal CSV row parser — quoted fields with embedded commas and escaped quotes
// (""), good enough for a simple email/firstName/lastName export. Not a full
// RFC 4180 implementation (no multi-line quoted fields) — real-world recipient
// CSVs essentially never need that, and pulling in a CSV library for one column
// shape isn't justified.
function parseCsvLine(line: string): string[] {
  const fields: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const char = line[i]!
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') { current += '"'; i++ } else { inQuotes = false }
      } else {
        current += char
      }
    } else if (char === '"') {
      inQuotes = true
    } else if (char === ',') {
      fields.push(current)
      current = ''
    } else {
      current += char
    }
  }
  fields.push(current)
  return fields.map((f) => f.trim())
}

export type ParsedRecipients = { parsed: CampaignRecipientInput[]; skippedRows: string[] }

// Accepts either a headered CSV (an "email" column, optionally "firstName"/
// "first_name"/"first name" and the last-name equivalents, any order) or a
// headerless one — assumed positional as email,firstName,lastName, matching the
// same convention the manual-paste box already uses.
export function parseCsvRecipients(text: string): ParsedRecipients {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
  if (lines.length === 0) return { parsed: [], skippedRows: [] }

  const firstRowFields = parseCsvLine(lines[0]!)
  const looksLikeHeader = firstRowFields.some((f) => /^email$/i.test(f))

  let emailCol = 0
  let firstCol = 1
  let lastCol = 2
  let dataLines = lines

  if (looksLikeHeader) {
    const headers = firstRowFields.map((h) => h.toLowerCase())
    emailCol = headers.findIndex((h) => h === 'email')
    firstCol = headers.findIndex((h) => h === 'firstname' || h === 'first_name' || h === 'first name')
    lastCol = headers.findIndex((h) => h === 'lastname' || h === 'last_name' || h === 'last name')
    dataLines = lines.slice(1)
  }

  const parsed: CampaignRecipientInput[] = []
  const skippedRows: string[] = []

  for (const line of dataLines) {
    const fields = parseCsvLine(line)
    const email = (fields[emailCol] ?? '').trim()
    if (!email || !EMAIL_RE.test(email)) { skippedRows.push(line); continue }
    const firstName = firstCol >= 0 ? fields[firstCol]?.trim() : undefined
    const lastName = lastCol >= 0 ? fields[lastCol]?.trim() : undefined
    parsed.push({ email, ...(firstName ? { firstName } : {}), ...(lastName ? { lastName } : {}) })
  }

  return { parsed, skippedRows }
}

// Same {{token}} substitution as the backend's renderTemplate (campaign-send-
// service.ts) — kept in exact sync so the builder's live preview is an honest
// representation of what will actually be sent, not an approximation.
export function renderMergeFields(template: string, fields: { firstName?: string | undefined; lastName?: string | undefined; email?: string | undefined }): string {
  const lookup: Record<string, string | undefined> = fields
  return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) => lookup[key] ?? match)
}
